/**
 * downloadUtils.js
 *
 * Centralised download helpers that handle the iOS Safari incompatibility.
 *
 * WHY THIS EXISTS
 * ---------------
 * iOS Safari does not support:
 *   - The `download` attribute on <a> tags (silently ignored)
 *   - Opening blob:// URLs in new tabs (window.open with a blob URL is a no-op)
 *
 * This means the standard "fetch → blob → createObjectURL → <a>.click()" pattern
 * shows 100% progress on iOS but never saves a file.
 *
 * The correct approach on iOS is to navigate the browser directly to a URL that
 * carries its own auth (e.g. a presigned S3 URL) and returns
 * Content-Disposition: attachment. iOS will then trigger its native share/download sheet.
 *
 * REQUIRED BACKEND ENDPOINTS
 * --------------------------
 * Two lightweight endpoints must exist that return a short-lived presigned URL as JSON:
 *
 *   GET /videos/download-url/{videoId}
 *     → { "url": "https://s3.amazonaws.com/...?X-Amz-Signature=..." }
 *
 *   GET /recording/{recordingId}/download-url
 *     → { "url": "https://s3.amazonaws.com/...?X-Amz-Signature=..." }
 *
 * Both endpoints require the standard Bearer auth header (handled by the axios
 * interceptor). The presigned URL they return is safe to expose to the client because
 * it is time-limited (typically 5–15 minutes) and scoped to a single object.
 */

import api from './api';

// ── iOS detection ──────────────────────────────────────────────────────────────

/**
 * Returns true when running inside iOS Safari (iPhone / iPad / iPod).
 * Covers both legacy and modern iPadOS user-agent strings.
 */
export const isIOS = () => {
  if (typeof navigator === 'undefined') return false;
  // Modern iPadOS reports itself as "Macintosh" but exposes touch support
  const isIPad = /Macintosh/.test(navigator.userAgent) && navigator.maxTouchPoints > 1;
  return (/iPad|iPhone|iPod/.test(navigator.userAgent) || isIPad) && !window.MSStream;
};

// ── iOS download path ──────────────────────────────────────────────────────────

/**
 * On iOS, navigate the current tab to a direct (presigned) URL.
 * The S3 presigned URL already carries its own auth in query params, so no
 * Authorization header is needed. iOS will present its native download/share sheet.
 */
export const iosNavigateToDownload = (url) => {
  window.top.location.href = url;
};

// ── Backend URL helpers ────────────────────────────────────────────────────────

/**
 * Ask the backend for a short-lived presigned S3 URL for a video download.
 * Uses the normal axios instance so the Bearer token is attached automatically.
 */
export const fetchVideoDownloadUrl = async (videoId) => {
  const response = await api.get(`/videos/download-url/${videoId}`);
  return response.data.url;
};

/**
 * Ask the backend for a short-lived presigned S3 URL for a recording download.
 */
export const fetchRecordingDownloadUrl = async (recordingId) => {
  const response = await api.get(`/recording/${recordingId}/download-url`);
  return response.data.url;
};

// ── Desktop / Android download path ───────────────────────────────────────────

/**
 * Trigger a file download on non-iOS browsers using the standard
 * blob → createObjectURL → <a download> approach.
 */
export const triggerBlobDownload = (blob, filename) => {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', filename);
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};
